/**
 * `@workspace/strings/web` — the control panel, marketing site, and web player.
 *
 * Composes every UI namespace (but NOT server errors or email copy) into one
 * scoped `t`. React components: `import { t } from "@workspace/strings/web"`.
 */
import { createT, getLocale, setLocale } from "../core.ts";
import { useT } from "../react.ts";

import { common } from "../en/common.ts";
import { channels } from "../en/channels.ts";
import { player } from "../en/player.ts";
import { meta } from "../en/meta.ts";
import { nav } from "../en/nav.ts";
import { consent } from "../en/consent.ts";
import { auth } from "../en/auth.ts";
import { dashboard } from "../en/dashboard.ts";
import { sites } from "../en/sites.ts";
import { fleet } from "../en/fleet.ts";
import { audiences } from "../en/audiences.ts";
import { schedules } from "../en/schedules.ts";
import { library } from "../en/library.ts";
import { playlists } from "../en/playlists.ts";
import { broadcast } from "../en/broadcast.ts";
import { team } from "../en/team.ts";
import { settings } from "../en/settings.ts";
import { billing } from "../en/billing.ts";
import { reports } from "../en/reports.ts";
import { support } from "../en/support.ts";
import { account } from "../en/account.ts";
import { onboarding } from "../en/onboarding.ts";
import { marketplace } from "../en/marketplace.ts";
import { fulfilment } from "../en/fulfilment.ts";
import { adminCustomers } from "../en/adminCustomers.ts";
import { adminOps } from "../en/adminOps.ts";
import { permissions } from "../en/permissions.ts";
import { legal } from "../en/legal.ts";
import { marketing } from "../en/marketing.ts";
import { downloads } from "../en/downloads.ts";
import { welcome } from "../en/welcome.ts";
import { playerUi } from "../en/playerUi.ts";
import { webMisc } from "../en/webMisc.ts";

const en = {
  common,
  channels,
  player,
  meta,
  nav,
  consent,
  auth,
  dashboard,
  sites,
  fleet,
  audiences,
  schedules,
  library,
  playlists,
  broadcast,
  team,
  settings,
  billing,
  reports,
  support,
  account,
  onboarding,
  marketplace,
  fulfilment,
  adminCustomers,
  adminOps,
  permissions,
  legal,
  marketing,
  downloads,
  welcome,
  playerUi,
  webMisc,
} as const;
const catalogs = { en } as const;

export const t = createT<typeof en>(
  () => catalogs[getLocale() as keyof typeof catalogs] ?? en,
);

export { setLocale, getLocale, useT };
export const strings = en;
export type WebStrings = typeof en;
